import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Eraser, PenLine, Trash2 } from "lucide-react";

/**
 * Lienzo simple para dibujar a mano (mouse/dedo) el diseño de un hierro de marcar.
 * Se guarda como PNG base64 en `value` (string) y se reporta vía onChange.
 */
export function IronBrandCanvas({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const initCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1c1917";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  };

  // Ref callback: corre exactamente cuando el <canvas> se monta dentro del
  // Dialog (que renderiza vía Portal), evitando la condición de carrera de
  // un useEffect leyendo canvasRef.current antes de que el nodo exista.
  const setCanvasNode = (node) => {
    canvasRef.current = node;
    if (node) initCanvas(node);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: ((point.clientX - rect.left) / rect.width) * canvas.width,
      y: ((point.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const save = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange(dataUrl);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-3" data-testid="iron-brand-canvas">
      {value ? (
        <img
          src={value}
          alt="Hierro dibujado"
          className="h-16 w-24 rounded-md border border-border object-contain bg-white"
          data-testid="iron-brand-drawing-thumb"
        />
      ) : (
        <div className="h-16 w-24 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
          Sin dibujo
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="iron-brand-draw-button">
          <PenLine className="h-3.5 w-3.5 mr-1.5" /> {value ? "Editar dibujo" : "Dibujar hierro"}
        </Button>
        {value && (
          <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => onChange(null)} data-testid="iron-brand-clear-button">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Quitar
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dibujar hierro de marcar</DialogTitle>
          </DialogHeader>
          <canvas
            ref={setCanvasNode}
            width={320}
            height={220}
            className="w-full rounded-md border border-border touch-none bg-white"
            data-testid="iron-brand-drawing-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={clear} data-testid="iron-brand-canvas-clear">
              <Eraser className="h-4 w-4 mr-1.5" /> Limpiar
            </Button>
            <Button type="button" onClick={save} data-testid="iron-brand-canvas-save">Guardar dibujo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
